import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { FileSpreadsheet } from 'lucide-react'
import toast from 'react-hot-toast'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { generatePathDatacentral, paths } from '@/utils/path'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { resources, scopes } from '@/utils/permission'
import { useSurveyList } from '../../../hooks/use-survey-list'
import { exportSurveyRecords } from '../../../services'
import Filters from './Filters'

const SurveyList = () => {
    const navigate = useNavigate()
    const { permissions, isAdmin } = usePermissionsStore()

    const {
        surveys,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        searchInputProps,
        columns,
        effectiveQueryParams,
        isError,
        isExporting,
        setIsExporting,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useSurveyList()

    const surveyScopes =
        permissions.find(
            (permission) => permission?.rsname === resources.surveys
        )?.scopes || []
    const canCreateSurvey = isAdmin || surveyScopes.includes(scopes.create)
    const canExportSurvey = isAdmin || surveyScopes.includes(scopes.index)
    const canExport =
        canExportSurvey &&
        !isFetching &&
        params.q === effectiveQueryParams.q &&
        !isError &&
        surveys.length > 0 &&
        !isExporting

    const handleExport = async () => {
        if (!canExport) return
        setIsExporting(true)
        try {
            const blob = await exportSurveyRecords(effectiveQueryParams)
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'survey-report.xlsx'
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            toast.success(t('surveys.export_success'))
        } catch {
            toast.error(t('surveys.export_error'))
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className="mt-2 flex flex-col gap-2">
            <Paper>
                <div className="p-2 flex items-center justify-between gap-2">
                    <Filters control={control} />
                    {canCreateSurvey && (
                        <Button
                            onClick={() =>
                                navigate(
                                    generatePathDatacentral(
                                        paths.surveyRecordCreate,
                                        {}
                                    )
                                )
                            }
                        >
                            {t('create')}
                        </Button>
                    )}
                    {canExportSurvey && (
                        <Button
                            variant="outline"
                            disabled={!canExport}
                            onClick={handleExport}
                        >
                            <FileSpreadsheet className="mr-2 h-4 w-4" />
                            {t(
                                isExporting
                                    ? 'surveys.exporting'
                                    : 'surveys.export_excel'
                            )}
                        </Button>
                    )}
                </div>
            </Paper>

            <Paper>
                {stateComponent || (
                    <DataTableCustomized
                        columns={columns}
                        rows={surveys}
                        rowKey={(row) => String(row?.survey_record_id)}
                        size="small"
                        resizable
                        loading={isFetching && !pagination}
                        isFetching={isFetching}
                        emptyMessage={t('no_data', 'No data')}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                        pagination={{
                            page: params.page || 1,
                            pageSize: params.page_size || 10,
                            total: pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                            pageSizeOptions: [10, 20, 50],
                        }}
                        toolbar={{
                            search: {
                                ...searchInputProps,
                                placeholder: t(
                                    'search_by_customer_name',
                                    'Search by customer name'
                                ),
                            },
                            settings: {
                                show: true,
                                storageKey: 'surveys-list',
                            },
                        }}
                    />
                )}
            </Paper>
        </div>
    )
}

export default SurveyList
